import React, { useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { LogoWord } from "../components/Brand";

function formatErr(d) {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

export default function LoginPage() {
  const { user, loading: authLoading, isAuthenticated, login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const params = new URLSearchParams(location.search);
  const nextPath = params.get("next") || location.state?.from || null;
  const quoteSku = params.get("quote");
  const serviceId = params.get("service");
  const postLoginTarget = (() => {
    if (nextPath) {
      const url = new URL(nextPath, window.location.origin);
      if (quoteSku) url.searchParams.set("quote", quoteSku);
      if (serviceId) url.searchParams.set("service", serviceId);
      return url.pathname + url.search;
    }
    if (quoteSku) return `/portal?quote=${encodeURIComponent(quoteSku)}`;
    if (serviceId) return `/portal?service=${encodeURIComponent(serviceId)}`;
    return null;
  })();

  if (authLoading) {
    return <div className="pt-40 text-center text-muted-foreground" data-testid="login-auth-loading">Loading…</div>;
  }

  if (isAuthenticated) {
    return <Navigate to={postLoginTarget || (user.role === "admin" ? "/admin" : "/portal")} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back.");
      nav(postLoginTarget || (u?.role === "admin" ? "/admin" : "/portal"));
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-16 grid grid-cols-1 lg:grid-cols-2 relative overflow-hidden" data-testid="login-page">
      <div className="hidden lg:block absolute inset-y-0 left-0 w-1/2 aurora-glow opacity-70 pointer-events-none" />
      <div className="flex items-center justify-center p-10">
        <motion.form
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          onSubmit={onSubmit}
          className="w-full max-w-md space-y-6 p-8 clip-frame border border-border bg-background"
        >
          <LogoWord />
          <div>
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Client Portal</div>
            <h1 className="display display-section text-4xl leading-tight mt-2">Welcome back.</h1>
          </div>
          <div className="space-y-3">
            <Input required type="email" placeholder="Work email" value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="login-email" className="rounded-sm h-12" />
            <Input required type="password" placeholder="Password" value={password} onChange={(e)=>setPassword(e.target.value)} data-testid="login-password" className="rounded-sm h-12" />
          </div>
          <Button type="submit" disabled={loading} className="btn-gold rounded-sm w-full h-12 uppercase font-mono-2 text-xs tracking-widest" data-testid="login-submit">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
          <div className="text-sm text-muted-foreground text-center">
            New client? <Link to="/register" className="text-[color:var(--gold)] underline">Create an account</Link>
          </div>
        </motion.form>
      </div>
      <div className="hidden lg:flex flex-col justify-between p-14 border-l border-border bg-secondary/40 relative">
        <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Portal · v1.0</div>
        <div>
          <div className="chapter-num">B2B</div>
          <h2 className="display display-section text-5xl leading-[0.9] tracking-[-0.03em] mt-6">
            Track every quote,<br/><em className="italic text-[color:var(--gold)]">every panel,</em><br/>every kilowatt.
          </h2>
          <p className="mt-6 text-steel-text max-w-md">Request quotes, follow order stages and audit invoices — all inside one accountable workspace.</p>
        </div>
        <div className="text-xs text-muted-foreground">© DEBSUN 2026 — Intelligence in Motion</div>
      </div>
    </div>
  );
}
